import React, { createContext, useEffect, useState, useContext } from 'react';
import { toast } from 'react-toastify';

import ToastContent from '../components/ToastContent';
import { formatLeadTimeLabel } from '../utils/leadTime';
import {
  CART_ADD_TOAST_KIND,
  CartToastTransition,
} from '../utils/cartToast';

const CartContext = createContext();
const CART_STORAGE_KEY = 'glace-cart-items-v1';

const readStoredCartItems = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const storedItems = window.localStorage.getItem(CART_STORAGE_KEY);

    if (!storedItems) {
      return [];
    }

    const parsedItems = JSON.parse(storedItems);
    return Array.isArray(parsedItems) ? parsedItems : [];
  } catch (error) {
    console.error('Não foi possível recuperar o carrinho salvo:', error);
    return [];
  }
};

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => readStoredCartItems());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Não foi possível salvar o carrinho:', error);
    }
  }, [cartItems]);

  const addToCart = (product) => {
    const leadTimeLabel = formatLeadTimeLabel(product?.minimumLeadTimeMinutes);
    const quantityToAdd = Math.max(1, Number(product?.quantity) || 1);

    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);

      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, ...product, quantity: item.quantity + quantityToAdd }
            : item
        );
      }

      return [...prevItems, { ...product, quantity: quantityToAdd }];
    });

    toast(
      <ToastContent
        productName={String(product?.nome || '')}
        leadTimeLabel={leadTimeLabel}
      />,
      {
        closeButton: true,
        autoClose: leadTimeLabel ? 7000 : 4500,
        data: { kind: CART_ADD_TOAST_KIND },
        transition: CartToastTransition,
        className: 'cart-add-toast',
      },
    );
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
  };

  const increaseQuantity = (productId) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  const decreaseQuantity = (productId) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId
          ? { ...item, quantity: Math.max(1, item.quantity - 1) }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
